import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useTheme } from '../contexts/ThemeContext';
import {
    Plane, Shield, ChevronRight, Globe, Users, BarChart3,
    Wrench, Loader2, Sun, Moon, Check, Target,
    ShieldCheck, ArrowRight, AlertTriangle,
    MonitorCheck, Headset, ClipboardList,
    Ticket, Radio, Fuel, Zap, Lock, Brain,
    ChevronDown, Sparkles, Menu, X,
    Package, Timer, UserCheck, Clipboard, CheckCircle, Truck, Activity,
    HelpCircle, Rocket, Database, History, Quote
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/logo.svg';

const airlineModules = [
    { icon: Plane, title: 'Control de Vuelos', desc: 'Programación, seguimiento diario, timeline operacional y bitacora de vuelo', color: 'sky' },
    { icon: Users, title: 'Tripulaciones & FDTL', desc: 'Asignacion de crew, alertas FDTL automáticas (FAA/EASA/DGAC), duty records', color: 'blue' },
    { icon: Wrench, title: 'Mantenimiento', desc: 'Ordenes de trabajo, diferidos MEL, reportes del capitan y tracking', color: 'amber' },
    { icon: Ticket, title: 'Ventas & Reservaciones', desc: 'Reservaciones por vuelo, check-in, ocupacion estimada en tiempo real', color: 'emerald' },
    { icon: Radio, title: 'Mensajes Operacionales', desc: 'Broadcast a todas las áreas: demoras, salidas, llegadas, IROPS', color: 'violet' },
    { icon: BarChart3, title: 'Estadisticas & Revenue', desc: 'OTP, utilizacion de flota, revenue & yield, horas de tripulación', color: 'rose' },
    { icon: ShieldCheck, title: 'SMS Seguridad', desc: 'Sistema de gestión de seguridad operacional (OACI Doc 9859)', color: 'emerald' },
    { icon: Fuel, title: 'Despacho & Rampa', desc: 'Autorización de vuelos, checklist de rampa, combustible y carga', color: 'orange' },
    { icon: Brain, title: 'Agentes IA', desc: '4 asistentes IA especializados: AURA, COPILOTO, WATCHDOG, SCRIBE', color: 'violet' },
];

const ghModules = [
    { icon: Clipboard, title: 'Turnaround Digital', desc: 'Checklist de 13 servicios con cronometro automático y timestamps', color: 'sky' },
    { icon: Users, title: 'Equipos Dinamicos', desc: 'Lideres rotativos, equipos flexibles por turno sin registrar cada agente', color: 'blue' },
    { icon: MonitorCheck, title: 'Dashboard Operacional', desc: 'Vista en tiempo real de todos los turnarounds con semaforo y KPIs', color: 'amber' },
    { icon: UserCheck, title: 'Asignacion por Supervisor', desc: 'El supervisor asigna vuelos a lideres disponibles en turno', color: 'emerald' },
    { icon: Timer, title: 'Control de Tiempos', desc: 'Tiempo de turnaround, servicios individuales y tiempo promedio', color: 'violet' },
    { icon: BarChart3, title: 'Reportes & SLA', desc: 'Cumplimiento por aerolínea cliente, tiempos promedio y rendimiento', color: 'rose' },
];

const ghServices = [
    'Guiado (Marshalling)', 'Calzas y Conos', 'GPU / Aire Acondicionado',
    'Descarga de Equipaje', 'Descarga de Carga', 'Limpieza de Cabina',
    'Agua Potable', 'Aguas Residuales', 'Carga de Equipaje',
    'Carga / Correo', 'Pushback / Remolque', 'Seguridad en Rampa', 'Coordinacion'
];

const airlinePlans = [
    { name: 'Starter', price: '$390', period: '/aeronave al mes', limits: [['1–5', 'Aeronaves'], ['15', 'Usuarios'], ['200', 'Vuelos/mes']], features: ['Módulos base: Vuelos + Operaciones', 'Add-ons opcionales (Crew, Mtto, IA)', 'Soporte por email', '50 GB almacenamiento'], highlight: false },
    { name: 'Professional', price: '$320', period: '/aeronave al mes', limits: [['6–15', 'Aeronaves'], ['40', 'Usuarios'], ['800', 'Vuelos/mes']], features: ['Módulos base: Vuelos + Operaciones', 'Add-ons opcionales (Crew, Mtto, IA, Predictivo, Revenue)', 'API Access', 'Soporte prioritario', '200 GB almacenamiento'], highlight: true },
    { name: 'Enterprise', price: '$260', period: '/aeronave al mes', limits: [['16+', 'Aeronaves'], ['100', 'Usuarios'], ['2,500', 'Vuelos/mes']], features: ['Módulos base: Vuelos + Operaciones', 'Add-ons opcionales (todos los módulos)', 'Integraciones custom', 'Soporte dedicado', 'SLA 99.9%', '1 TB almacenamiento'], highlight: false },
];

const ghPlans = [
    { name: 'GH Starter', price: '$199', period: '/mes', limits: [['10', 'Usuarios'], ['300', 'Turns/mes'], ['5', 'Clientes']], features: ['Turnaround management', 'Dashboard operacional', 'Reportes basicos'], highlight: false },
    { name: 'GH Professional', price: '$499', period: '/mes', limits: [['30', 'Usuarios'], ['1,000', 'Turns/mes'], ['15', 'Clientes']], features: ['Todos los módulos GH', 'Reportes SLA', 'Gestión de equipos', 'API Access'], highlight: true },
    { name: 'GH Enterprise', price: '$999', period: '/mes', limits: [['80', 'Usuarios'], ['5,000', 'Turns/mes'], ['50', 'Clientes']], features: ['Integraciones custom', 'Soporte dedicado', 'SLA 99.9%', '1 TB almacenamiento'], highlight: false },
];

const stats = [
    { value: '2', label: 'Productos' },
    { value: '15+', label: 'Módulos' },
    { value: 'FAA', label: 'Estándares' },
    { value: 'IA', label: 'Asistente Integrado' },
];

// Seccion "Como empezar" — pasos del proceso de implementacion
const onboardingSteps = [
    { icon: Target, title: 'Eliges tu plan y módulos', desc: 'Según el tamaño de tu flota y tus operaciones — plan base más los add-ons que realmente necesitas, nada de más.' },
    { icon: Database, title: 'Configuramos tu flota y tripulación', desc: 'Te acompañamos cargando aeronaves, rutas, tripulación y habilitaciones — no empiezas desde una pantalla vacía.' },
    { icon: Users, title: 'Capacitamos a tu equipo', desc: 'Sesiones prácticas por rol: despacho, rampa, mantenimiento, mostrador — cada quien aprende solo lo que usa.' },
    { icon: Rocket, title: 'Empiezas a operar', desc: 'Tu equipo trabaja en AeroOps desde el primer día, con acompañamiento cercano durante las primeras semanas.' },
];

// Seccion de confianza y cumplimiento — separada de las tarjetas de modulos
const trustPoints = [
    { icon: Lock, title: 'Aislamiento total de datos', desc: 'Arquitectura multi-tenant real: los datos de tu aerolínea nunca se mezclan con los de otro cliente de AeroOps, a ningún nivel del sistema.' },
    { icon: History, title: 'Historial auditable', desc: 'Cada decisión operacional queda registrada — quién la tomó, cuándo, y por qué. No solo para cumplimiento, para tu propia tranquilidad.' },
    { icon: Database, title: 'Respaldo continuo', desc: 'Tu información operacional se respalda de forma continua — no dependes de que alguien se acuerde de hacer una copia.' },
    { icon: ShieldCheck, title: 'Alineado a estándares reales', desc: 'Diseñado con referencia directa a DGAC, IOSA y FAA — no genérico, pensado para lo que un regulador realmente pide ver.' },
];

// FAQ
const faqItems = [
    { q: '¿Cuánto tiempo toma implementar AeroOps?', a: 'Depende del tamaño de tu flota, pero la mayoría de aerolíneas regionales quedan operando en pocas semanas — te acompañamos cargando tu flota, rutas y tripulación desde el primer día.' },
    { q: '¿Puedo migrar mis datos desde Excel o mi sistema actual?', a: 'Sí. Es justo la situación más común que vemos: aerolíneas operando con hojas de cálculo o varios programas sueltos que no se hablan entre sí. Te ayudamos a ordenar y cargar esa información en AeroOps.' },
    { q: '¿El soporte es en español?', a: 'Sí, el equipo de soporte y la plataforma completa están en español, pensados para operaciones en Latinoamérica.' },
    { q: '¿Mis datos están aislados de otras aerolíneas que usan AeroOps?', a: 'Completamente. Cada aerolínea tiene su propio espacio aislado — nunca se comparte ni se mezcla información entre distintos clientes de la plataforma.' },
    { q: '¿Puedo cambiar de plan o agregar módulos después?', a: 'Sí, en cualquier momento. Empiezas con lo que necesitas hoy y agregas módulos (Tripulaciones, Mantenimiento, IA, Predictivo, Revenue) conforme tu operación crece.' },
    { q: '¿Qué pasa si cancelo mi suscripción?', a: 'Puedes cancelar cuando quieras. Tu información permanece disponible durante el período ya pagado, y te avisamos con anticipación antes de cualquier cambio en tu acceso.' },
];

// Historia del fundador — "Por que AeroOps"
const founderStory = {
    quote: 'He trabajado muchos años como despachador de vuelos, supervisor de despacho y en Centro de Control de Operaciones (CCO). Con eso vino contacto directo con muchas áreas y muchas aerolíneas distintas — vi de cerca cómo trabajan, qué tienen y qué no tienen.',
    points: [
        'Las aerolíneas grandes suelen tener sus propios sistemas a la medida — las pequeñas y nacionales, casi nunca.',
        'Grandes y pequeñas por igual siguen usando hojas de cálculo para sus análisis operacionales.',
        'La mayoría trabaja con varios programas sueltos que no se hablan entre sí, repitiendo el mismo dato varias veces.',
    ],
    closing: 'AeroOps nace de haber vivido esas rotaciones, esas programaciones, esos vuelos, desde adentro — para acortar tiempos, responder rápido, y resolver dudas operacionales sin tener que saltar entre cinco pantallas distintas.',
};

// FASE C Audit 2: catalogo de paises (Latam prioritario, resto alfabetico)
const COUNTRIES = [
    'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica', 'Cuba', 'Ecuador',
    'El Salvador', 'Guatemala', 'Honduras', 'Mexico', 'Nicaragua', 'Panama', 'Paraguay',
    'Peru', 'Puerto Rico', 'Republica Dominicana', 'Uruguay', 'Venezuela',
    'Alemania', 'Australia', 'Austria', 'Belgica', 'Canada', 'China', 'Corea del Sur',
    'Emiratos Arabes Unidos', 'Espana', 'Estados Unidos', 'Filipinas', 'Francia', 'India',
    'Indonesia', 'Irlanda', 'Israel', 'Italia', 'Japon', 'Malasia', 'Nueva Zelanda',
    'Países Bajos', 'Polonia', 'Portugal', 'Reino Unido', 'Singapur', 'Sudafrica', 'Suecia',
    'Suiza', 'Tailandia', 'Turquia', 'Otro',
];

// ==== Elemento distintivo: pantalla de radar ATC animada (100% SVG/CSS, sin imagenes externas) ====
// Blips = posicion, delay y color de cada "aeronave" en el scope. Delay escalonado para que no
// parpadeen todas a la vez, como un radar real con trafico variado.
const RADAR_BLIPS = [
    { x: 62, y: 30, delay: '0s', color: '#0EA5E9' },
    { x: 30, y: 55, delay: '1.6s', color: '#22C55E' },
    { x: 75, y: 68, delay: '3.1s', color: '#0EA5E9' },
    { x: 45, y: 22, delay: '4.4s', color: '#F59E0B' },
    { x: 20, y: 32, delay: '5.7s', color: '#0EA5E9' },
    { x: 68, y: 48, delay: '2.3s', color: '#22C55E' },
];

function RadarScope({ className = '' }) {
    return (
        <div className={`relative ${className}`} aria-hidden="true">
            <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible">
                {/* Anillos concentricos */}
                {[46, 34, 22, 10].map((r) => (
                    <circle key={r} cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="0.4" className="text-sky-400" />
                ))}
                {/* Cruz de referencia N/S/E/O */}
                <line x1="50" y1="4" x2="50" y2="96" stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.3" className="text-sky-400" />
                <line x1="4" y1="50" x2="96" y2="50" stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.3" className="text-sky-400" />
                {/* Borde exterior */}
                <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="0.5" className="text-sky-400" />

                {/* Barrido giratorio (conic-gradient dentro de un foreignObject seria ideal, pero SVG puro con gradient radial-sector es mas compatible) */}
                <g style={{ transformOrigin: '50px 50px', animation: 'radarSweep 5.5s linear infinite' }}>
                    <defs>
                        <linearGradient id="sweepGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0" />
                            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.55" />
                        </linearGradient>
                    </defs>
                    <path d="M 50 50 L 50 4 A 46 46 0 0 1 88.3 27 Z" fill="url(#sweepGradient)" />
                </g>

                {/* Blips: aeronaves detectadas */}
                {RADAR_BLIPS.map((b, i) => (
                    <g key={i}>
                        <circle cx={b.x} cy={b.y} r="4" fill={b.color} opacity="0" style={{ animation: `radarBlipPing 4.6s ease-out infinite`, animationDelay: b.delay, transformOrigin: `${b.x}px ${b.y}px` }} />
                        <circle cx={b.x} cy={b.y} r="1.3" fill={b.color} opacity="0" style={{ animation: `radarBlip 4.6s ease-in-out infinite`, animationDelay: b.delay, transformOrigin: `${b.x}px ${b.y}px` }} />
                    </g>
                ))}
            </svg>
        </div>
    );
}

// ==== Horizonte de pista estilizado (ilustracion original CSS/SVG, sin fotografia de stock) ====
// Usado como fondo de seccion para dar variedad visual sin depender de imagenes externas
// con licencia incierta.
function OperationsCenter({ theme = 'dark' }) {
    const isDark = theme === 'dark';
    // Simula un war-room aeronautico con 3 pantallas mostrando modulos reales:
    // Flight Tracking (mapa de red de vuelos), Flight Status (tablero de salidas),
    // Turnaround Monitoring (dashboard de aeropuerto en tierra).
    const flights = [
        { fn: 'AN 407', from: 'GUA', eta: '18:42', status: 'ON TIME' },
        { fn: 'AN 218', from: 'MEX', eta: '19:15', status: 'ON TIME' },
        { fn: 'AN 512', from: 'MIA', eta: '19:28', status: 'DELAY' },
        { fn: 'AN 103', from: 'SAL', eta: '19:47', status: 'ON TIME' },
        { fn: 'AN 620', from: 'JFK', eta: '20:03', status: 'BOARDING' },
        { fn: 'AN 331', from: 'PTY', eta: '20:22', status: 'ON TIME' },
        { fn: 'AN 442', from: 'BOG', eta: '20:55', status: 'ON TIME' },
    ];
    const bars = [
        { g: 'A12 · AN407', pct: 92, color: '#22C55E' },
        { g: 'A14 · AN218', pct: 68, color: '#3B82F6' },
        { g: 'B03 · AN512', pct: 45, color: '#F59E0B' },
        { g: 'B05 · AN103', pct: 78, color: '#22C55E' },
        { g: 'C02 · AN620', pct: 100, color: '#22C55E' },
        { g: 'C04 · AN331', pct: 34, color: '#3B82F6' },
    ];
    return (
        <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden border border-slate-800 shadow-2xl" aria-hidden="true"
             style={{ background: isDark ? 'linear-gradient(180deg, #050810 0%, #0B111C 100%)' : 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}>
            {/* Ambient dim room */}
            <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(56,189,248,0.10) 0%, transparent 65%)' }} />

            {/* MONITOR GRID (3 pantallas + fila inferior con teclados/operadores estilizados) */}
            <div className="absolute inset-0 grid grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-5">
                {/* ==================== MONITOR 1 — FLIGHT TRACKING ==================== */}
                <div className="relative rounded-md bg-[#0A1628] border border-sky-900/60 overflow-hidden shadow-[0_0_30px_rgba(56,189,248,0.15)]">
                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-sky-900/50 bg-[#081020]">
                        <div className="flex items-center gap-1.5">
                            <Radio className="w-2.5 h-2.5 text-sky-400" />
                            <span className="text-[8px] sm:text-[9px] font-bold tracking-widest text-sky-300">FLIGHT TRACKING</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ animation: 'runwayLightPulse 1.4s ease-in-out infinite' }} />
                            <span className="text-[7px] sm:text-[8px] text-emerald-400 font-mono">LIVE</span>
                        </div>
                    </div>
                    <svg viewBox="0 0 200 130" className="w-full h-[calc(100%-24px)]" preserveAspectRatio="xMidYMid slice">
                        {/* Grid map background */}
                        <defs>
                            <pattern id="tk-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#0EA5E9" strokeWidth="0.15" opacity="0.35" />
                            </pattern>
                        </defs>
                        <rect width="200" height="130" fill="url(#tk-grid)" />
                        {/* Airport nodes */}
                        {[[30, 40], [70, 25], [110, 55], [150, 35], [170, 75], [50, 90], [95, 100], [140, 95], [40, 65], [125, 20]].map(([cx, cy], i) => (
                            <g key={i}>
                                <circle cx={cx} cy={cy} r="1.6" fill="#38BDF8" />
                                <circle cx={cx} cy={cy} r="4" fill="none" stroke="#38BDF8" strokeOpacity="0.35" style={{ animation: `radarBlipPing 2.6s ease-out infinite`, animationDelay: `${i * 0.25}s` }} />
                            </g>
                        ))}
                        {/* Route lines */}
                        {[[30, 40, 110, 55], [70, 25, 125, 20], [110, 55, 150, 35], [150, 35, 170, 75], [95, 100, 140, 95], [40, 65, 95, 100], [30, 40, 50, 90]].map(([x1, y1, x2, y2], i) => (
                            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#38BDF8" strokeWidth="0.4" strokeOpacity="0.55" strokeDasharray="2 2" />
                        ))}
                        {/* Moving aircraft (SVG use) */}
                        {[[55, 45, 15], [130, 60, -25], [155, 80, 90]].map(([cx, cy, rot], i) => (
                            <g key={i} transform={`translate(${cx}, ${cy}) rotate(${rot})`}>
                                <path d="M0,-3 L2,2 L0,1 L-2,2 Z" fill="#FBBF24" style={{ animation: 'radarBlip 3s ease-in-out infinite', animationDelay: `${i * 0.7}s` }} />
                            </g>
                        ))}
                    </svg>
                </div>

                {/* ==================== MONITOR 2 — FLIGHT STATUS BOARD ==================== */}
                <div className="relative rounded-md bg-[#0A1628] border border-sky-900/60 overflow-hidden shadow-[0_0_30px_rgba(56,189,248,0.15)]">
                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-sky-900/50 bg-[#081020]">
                        <div className="flex items-center gap-1.5">
                            <Plane className="w-2.5 h-2.5 text-sky-400" />
                            <span className="text-[8px] sm:text-[9px] font-bold tracking-widest text-sky-300">FLIGHT STATUS</span>
                        </div>
                        <span className="text-[7px] sm:text-[8px] text-slate-500 font-mono">FEB 20 · GUA</span>
                    </div>
                    <div className="p-1.5 sm:p-2">
                        {/* Header row */}
                        <div className="grid grid-cols-[1.3fr_1fr_1fr_1.4fr] gap-1 text-[7px] sm:text-[8px] font-bold text-slate-500 tracking-widest border-b border-slate-800 pb-1 mb-1">
                            <div>FLIGHT</div><div>FROM</div><div>ETA</div><div>STATUS</div>
                        </div>
                        {flights.map((f, i) => {
                            const statusColor = f.status === 'ON TIME' ? 'text-emerald-400' : f.status === 'DELAY' ? 'text-rose-400' : 'text-amber-400';
                            return (
                                <div key={i} className="grid grid-cols-[1.3fr_1fr_1fr_1.4fr] gap-1 text-[8px] sm:text-[9px] font-mono py-0.5 border-b border-slate-900/60">
                                    <div className="text-sky-300 font-bold">{f.fn}</div>
                                    <div className="text-slate-300">{f.from}</div>
                                    <div className="text-slate-300">{f.eta}</div>
                                    <div className={`${statusColor} font-bold flex items-center gap-1`}>
                                        <span className="w-1 h-1 rounded-full bg-current" style={{ animation: 'runwayLightPulse 1.8s ease-in-out infinite', animationDelay: `${i * 0.15}s` }} />
                                        {f.status}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ==================== MONITOR 3 — TURNAROUND MONITORING ==================== */}
                <div className="relative rounded-md bg-white border border-slate-300 overflow-hidden shadow-[0_0_30px_rgba(56,189,248,0.20)]">
                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-200 bg-slate-50">
                        <div className="flex items-center gap-1.5">
                            <Activity className="w-2.5 h-2.5 text-sky-600" />
                            <span className="text-[8px] sm:text-[9px] font-bold tracking-widest text-slate-700">TURNAROUND MONITORING</span>
                        </div>
                        <span className="text-[7px] sm:text-[8px] text-slate-400 font-mono">GH · GUA</span>
                    </div>
                    <div className="p-1.5 sm:p-2 space-y-1.5">
                        {/* KPI cards */}
                        <div className="grid grid-cols-3 gap-1">
                            <div className="rounded bg-slate-100 border border-slate-200 px-1.5 py-1">
                                <div className="text-[10px] sm:text-sm font-bold text-slate-900 font-mono leading-none">32</div>
                                <div className="text-[6px] sm:text-[7px] text-slate-500 uppercase tracking-widest mt-0.5">Turnarounds</div>
                            </div>
                            <div className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-1">
                                <div className="text-[10px] sm:text-sm font-bold text-emerald-700 font-mono leading-none">98%</div>
                                <div className="text-[6px] sm:text-[7px] text-emerald-600 uppercase tracking-widest mt-0.5">On Time</div>
                            </div>
                            <div className="rounded bg-sky-50 border border-sky-200 px-1.5 py-1">
                                <div className="text-[10px] sm:text-sm font-bold text-sky-700 font-mono leading-none">24</div>
                                <div className="text-[6px] sm:text-[7px] text-sky-600 uppercase tracking-widest mt-0.5">Completed</div>
                            </div>
                        </div>
                        {/* Gantt-style bars */}
                        <div className="space-y-0.5 pt-1">
                            {bars.map((b, i) => (
                                <div key={i} className="flex items-center gap-1">
                                    <div className="text-[6px] sm:text-[7px] text-slate-600 font-mono w-14 sm:w-16 truncate">{b.g}</div>
                                    <div className="flex-1 h-1 sm:h-1.5 bg-slate-200 rounded-sm overflow-hidden">
                                        <div className="h-full rounded-sm transition-all" style={{ width: `${b.pct}%`, background: b.color }} />
                                    </div>
                                    <div className="text-[6px] sm:text-[7px] font-mono font-bold w-5 text-right" style={{ color: b.color }}>{b.pct}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Scan lines overlay for CRT effect */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{
                backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.8), rgba(255,255,255,0.8) 1px, transparent 1px, transparent 3px)',
            }} />
            {/* Corner tag */}
            <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/40 border border-white/10 text-[7px] font-mono text-slate-300 tracking-widest">
                AEROOPS · OPS ROOM
            </div>
        </div>
    );
}

// Hook + wrapper para animaciones disparadas al hacer scroll (IntersectionObserver nativo,
// sin dependencias nuevas). stagger permite escalonar hijos de una grilla.
function Reveal({ children, className = '', as: Tag = 'div', delay = 0 }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Tag
            ref={ref}
            className={`reveal-on-scroll ${visible ? 'is-visible' : ''} ${className}`}
            style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
        >
            {children}
        </Tag>
    );
}

export default function LandingPage() {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const [leadForm, setLeadForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', country: '', fleet_size: '', message: '', source: 'website', service_type: 'airline' });
    const [submitting, setSubmitting] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeProduct, setActiveProduct] = useState('airlines');
    // FASE C Audit 2: estados independientes por tab (Productos / Modulos / Planes)
    const [activeModulesTab, setActiveModulesTab] = useState('airlines');
    const [activePlansTab, setActivePlansTab] = useState('airlines');
    // Auditoria 9 - Item 2: catalogo publico de add-ons (por tenant_type)
    const [airlineAddons, setAirlineAddons] = useState([]);
    const [ghAddons, setGhAddons] = useState([]);

    useEffect(() => {
        // Fetch add-ons desde /public/modules/catalog (sin auth) — Auditoria 9 - Items 1 y 2
        (async () => {
            try {
                const [a, g] = await Promise.all([
                    axios.get(`${API}/public/modules/catalog?tenant_type=airline`),
                    axios.get(`${API}/public/modules/catalog?tenant_type=ground_handler`),
                ]);
                setAirlineAddons(a.data.addons || []);
                setGhAddons(g.data.addons || []);
            } catch (e) {
                console.warn('No se pudo cargar el catalogo público:', e?.message);
            }
        })();
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...leadForm, fleet_size: leadForm.fleet_size ? parseInt(leadForm.fleet_size) : null };
            await axios.post(`${API}/public/leads`, payload);
            toast.success('Gracias por tu interes. Te contactaremos pronto.');
            setLeadForm({ company_name: '', contact_name: '', email: '', phone: '', country: '', fleet_size: '', message: '', source: 'website', service_type: 'airline' });
        } catch { toast.error('Error al enviar. Intenta nuevamente.'); }
        finally { setSubmitting(false); }
    };

    const scrollTo = (id) => { setMobileMenu(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

    const colorMap = { sky: 'bg-sky-500/10 text-sky-500', blue: 'bg-blue-500/10 text-blue-500', amber: 'bg-amber-500/10 text-amber-500', emerald: 'bg-emerald-500/10 text-emerald-500', violet: 'bg-violet-500/10 text-violet-500', rose: 'bg-rose-500/10 text-rose-500', orange: 'bg-orange-500/10 text-orange-500', cyan: 'bg-cyan-500/10 text-cyan-500' };

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
            {/* NAV */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-background/95 border-b border-border/40 shadow-sm' : 'bg-transparent'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <img src={LOGO_URL} alt="AeroOps" className="w-9 h-9 object-contain" />
                        <div className="flex flex-col">
                            <span className="text-lg font-bold tracking-tight uppercase font-['Barlow_Condensed'] leading-none">AeroOps</span>
                            <span className="text-[9px] text-muted-foreground tracking-[0.2em] uppercase leading-none">Technologies</span>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        {[['productos', 'Productos'], ['módulos', 'Módulos'], ['planes', 'Planes'], ['contact', 'Contacto']].map(([id, label]) => (
                            <button key={id} onClick={() => scrollTo(id)} className="hover:text-primary transition-colors duration-200 relative group">
                                {label}<span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-primary transition-all duration-200 group-hover:w-full" />
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted transition-colors duration-200" data-testid="landing-theme-toggle" aria-label="Toggle theme">
                            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <Button size="sm" onClick={() => navigate('/login')} data-testid="landing-cta-login" className="bg-amber-500 hover:bg-amber-600 text-black text-xs font-bold uppercase tracking-wider">
                            Acceder
                        </Button>
                        <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 hover:bg-muted rounded-lg" aria-label="Menu">
                            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
                {mobileMenu && (
                    <div className="md:hidden glass-panel border-t border-border/30 px-4 py-4 space-y-3">
                        {[['productos', 'Productos'], ['módulos', 'Módulos'], ['planes', 'Planes'], ['contact', 'Contacto']].map(([id, label]) => (
                            <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left text-sm font-bold uppercase tracking-wider text-muted-foreground hover:text-primary py-2">{label}</button>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="w-full text-xs font-bold uppercase tracking-wider">Acceder</Button>
                    </div>
                )}
            </nav>

            {/* HERO */}
            <section className="relative pt-28 pb-24 sm:pt-36 sm:pb-32 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(var(--primary)/0.12),transparent_70%)]" />
                <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[100px]" />
                {/* Cuadricula sutil de fondo, refuerza el tono "pantalla de instrumento" */}
                <div className="absolute inset-0 opacity-[0.035]" style={{
                    backgroundImage: 'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
                    backgroundSize: '48px 48px',
                }} />
                <div className="max-w-6xl mx-auto relative z-10">
                    <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-16 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold tracking-widest uppercase mb-8 animate-[fadeIn_0.6s_ease-out]">
                                <Sparkles className="w-3.5 h-3.5" /> Dos Productos, Una Plataforma
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] leading-[1.1] animate-[fadeIn_0.8s_ease-out]" data-testid="landing-hero-title">
                                Control Operacional<br />
                                <span className="text-primary relative">
                                    Aeronautico
                                    <span className="absolute -bottom-2 left-0 w-full h-[3px] bg-primary/40 rounded-full" />
                                </span>
                            </h1>
                            <p className="mt-8 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed animate-[fadeIn_1s_ease-out]">
                                Plataforma integral para <strong className="text-foreground">aerolineas</strong> y <strong className="text-foreground">empresas de asistencia en tierra</strong>. Gestion operacional unificada, alineada con estandares <strong className="text-foreground">FAA</strong> y <strong className="text-foreground">OACI</strong>.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row items-start gap-4 animate-[fadeIn_1.2s_ease-out]">
                                <Button size="lg" onClick={() => { setActiveProduct('airlines'); scrollTo('productos'); }} className="btn-primary text-sm px-8 py-3 group" data-testid="landing-cta-airlines">
                                    <Plane className="w-4 h-4 mr-2" />AeroOps Airlines <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                                </Button>
                                <Button size="lg" variant="outline" onClick={() => { setActiveProduct('ground'); scrollTo('productos'); }} data-testid="landing-cta-ground" className="text-sm px-8 py-3 group">
                                    <Truck className="w-4 h-4 mr-2" />AeroOps Ground <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                                </Button>
                            </div>
                        </div>
                        <div className="hidden lg:flex items-center justify-center">
                            <div className="relative w-80 h-80">
                                <div className="absolute inset-0 bg-primary/10 rounded-full blur-[70px] scale-125" />
                                <RadarScope className="absolute inset-0 z-10" />
                                <div className="absolute inset-0 flex items-center justify-center z-20">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-background/80 rounded-full blur-md scale-150" />
                                        <img src={LOGO_URL} alt="AeroOps Technologies" className="w-16 h-16 object-contain relative drop-shadow-[0_0_20px_rgba(14,165,233,0.35)]" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-8 animate-[fadeIn_1.4s_ease-out]">
                        {stats.map((s) => (
                            <div key={s.label} className="text-center sm:text-left p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/30">
                                <div className="text-2xl sm:text-3xl font-bold font-mono text-primary">{s.value}</div>
                                <div className="text-xs text-muted-foreground mt-1 font-bold uppercase tracking-wider">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block"><ChevronDown className="w-5 h-5 text-muted-foreground/50" /></div>
            </section>

            {/* PRODUCTS OVERVIEW */}
            <section id="productos" className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase mb-4">
                            <Target className="w-3.5 h-3.5" /> Nuestros Productos
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="landing-products-title">
                            Dos Soluciones Especializadas
                        </h2>
                        <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto">Cada producto es independiente, diseñado para las necesidades operacionales especificas de cada tipo de empresa.</p>
                    </Reveal>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Airlines Card */}
                        <Reveal delay={0}>
                        <Card className={`card-aviation relative overflow-hidden cursor-pointer group transition-all duration-300 ${activeProduct === 'airlines' ? 'border-sky-500/50 shadow-lg shadow-sky-500/5' : 'hover:border-sky-500/30'}`}
                            onClick={() => setActiveProduct('airlines')} data-testid="product-airlines">
                            <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" />
                            <CardContent className="p-8">
                                <div className="flex items-center gap-4 mb-5">
                                    <div className="w-14 h-14 rounded-xl bg-sky-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                        <Plane className="w-7 h-7 text-sky-500" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide">AeroOps Airlines</h3>
                                        <p className="text-xs text-muted-foreground">Para aerolineas y operadores aereos</p>
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                                    Gestion integral de operaciones aereas: vuelos, tripulaciones, mantenimiento, despacho, ventas, SMS y mas. Con alertas FDTL automaticas y 4 agentes IA especializados.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {['9 Módulos', '14 Roles', 'FDTL', '4 Agentes IA', 'Revenue & Yield'].map(t => (
                                        <span key={t} className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-500 text-[10px] font-bold">{t}</span>
                                    ))}
                                </div>
                                <div className="mt-5 flex items-center gap-2 text-xs text-sky-500 font-bold">
                                    <span>Desde $260 por aeronave al mes</span><ArrowRight className="w-3 h-3" />
                                </div>
                            </CardContent>
                        </Card>
                        </Reveal>

                        {/* Ground Handling Card */}
                        <Reveal delay={120}>
                        <Card className={`card-aviation relative overflow-hidden cursor-pointer group transition-all duration-300 ${activeProduct === 'ground' ? 'border-amber-500/50 shadow-lg shadow-amber-500/5' : 'hover:border-amber-500/30'}`}
                            onClick={() => setActiveProduct('ground')} data-testid="product-ground">
                            <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" />
                            <CardContent className="p-8">
                                <div className="flex items-center gap-4 mb-5">
                                    <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                        <Truck className="w-7 h-7 text-amber-500" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide">AeroOps Ground</h3>
                                        <p className="text-xs text-muted-foreground">Para empresas de asistencia en tierra</p>
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                                    Gestion de turnarounds con checklist digital de 13 servicios, equipos dinamicos, asignacion por supervisor y dashboard operacional en tiempo real.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {['13 Servicios', 'Equipos Dinamicos', 'KPIs', 'SLA Tracking', 'Multi-Aerolínea'].map(t => (
                                        <span key={t} className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-bold">{t}</span>
                                    ))}
                                </div>
                                <div className="mt-5 flex items-center gap-2 text-xs text-amber-500 font-bold">
                                    <span>Desde $199/mes</span><ArrowRight className="w-3 h-3" />
                                </div>
                            </CardContent>
                        </Card>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MODULES — tabbed */}
            <section id="módulos" className="py-20 px-4 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <Tabs value={activeModulesTab} onValueChange={setActiveModulesTab} className="w-full">
                        <Reveal className="text-center mb-10">
                            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] mb-6" data-testid="landing-modules-title">
                                Modulos y Funcionalidades
                            </h2>
                            <TabsList className="mx-auto">
                                <TabsTrigger value="airlines" data-testid="modules-tab-airlines"><Plane className="w-3.5 h-3.5 mr-1.5" />Airlines</TabsTrigger>
                                <TabsTrigger value="ground" data-testid="modules-tab-ground"><Truck className="w-3.5 h-3.5 mr-1.5" />Ground Handling</TabsTrigger>
                            </TabsList>
                        </Reveal>
                        <TabsContent value="airlines" className="mt-0">
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {airlineModules.map((m, i) => {
                                    const Icon = m.icon;
                                    return (
                                        <Reveal key={m.title} delay={(i % 3) * 90}>
                                        <Card className="card-aviation group" data-testid={`airline-module-${i}`}>
                                            <CardContent className="p-6">
                                                <div className={`w-11 h-11 rounded-lg ${colorMap[m.color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                                <h3 className="font-bold text-base font-['Barlow_Condensed'] uppercase tracking-wide">{m.title}</h3>
                                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
                                            </CardContent>
                                        </Card>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </TabsContent>
                        <TabsContent value="ground" className="mt-0">
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {ghModules.map((m, i) => {
                                    const Icon = m.icon;
                                    return (
                                        <Reveal key={m.title} delay={(i % 3) * 90}>
                                        <Card className="card-aviation group" data-testid={`gh-module-${i}`}>
                                            <CardContent className="p-6">
                                                <div className={`w-11 h-11 rounded-lg ${colorMap[m.color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                                <h3 className="font-bold text-base font-['Barlow_Condensed'] uppercase tracking-wide">{m.title}</h3>
                                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
                                            </CardContent>
                                        </Card>
                                        </Reveal>
                                    );
                                })}
                            </div>
                            {/* 13 Services Visual */}
                            <Reveal>
                            <Card className="card-aviation mt-8">
                                <CardContent className="p-6">
                                    <h3 className="font-bold text-sm font-['Barlow_Condensed'] uppercase tracking-wide mb-4 flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-amber-500" />13 Servicios en el Checklist Digital
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {ghServices.map((s, i) => (
                                            <span key={i} className="px-3 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs font-medium">{s}</span>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                            </Reveal>
                        </TabsContent>
                    </Tabs>
                </div>
            </section>

            {/* OPERACIONES REALES — sala de operaciones AeroOps (3 monitores con datos reales) */}
            <section className="relative py-24 px-4 overflow-hidden bg-slate-900 dark:bg-[#050810]">
                {/* Ambient background */}
                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 100% 60% at 50% 0%, rgba(56,189,248,0.10) 0%, transparent 65%)' }} />
                <div className="absolute inset-0 opacity-[0.04]" style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
                    backgroundSize: '48px 48px, 48px 48px',
                }} />
                <div className="max-w-6xl mx-auto relative z-10">
                    <Reveal>
                        <div className="text-center max-w-3xl mx-auto mb-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-400 text-xs font-bold tracking-widest uppercase mb-6 backdrop-blur-sm">
                                <ShieldCheck className="w-3.5 h-3.5" /> Cero Tolerancia a Errores
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] text-white leading-tight">
                                Construido para Operaciones<br />que No Pueden Fallar
                            </h2>
                            <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed max-w-xl mx-auto">
                                Cada modulo de AeroOps se disena pensando en el Centro de Control, la rampa y el despacho — no en una hoja de calculo. Trazabilidad completa, alertas que bloquean cuando deben bloquear, y un historial auditable de cada decision operacional.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={100}>
                        <OperationsCenter theme={theme} />
                    </Reveal>
                    <Reveal delay={200}>
                        <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg mx-auto">
                            {[['DGAC', 'Cumplimiento'], ['IOSA', 'Estándar'], ['FAA', 'Referencia']].map(([v, l]) => (
                                <div key={v} className="text-center p-3 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm">
                                    <div className="text-lg font-bold font-mono text-sky-400">{v}</div>
                                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{l}</div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* AI & TECHNOLOGY */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <Reveal>
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 text-violet-500 text-xs font-bold tracking-widest uppercase mb-6">
                                <Brain className="w-3.5 h-3.5" /> Tecnologia
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] leading-tight" data-testid="landing-ai-title">
                                IA y Cumplimiento<br />Normativo
                            </h2>
                            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                                AeroOps integra <strong className="text-foreground">asistentes IA (GPT-5.2)</strong> que conocen tu operacion y alertas automaticas FDTL con soporte para <strong className="text-foreground">4 regulaciones internacionales</strong>.
                            </p>
                            <div className="mt-8 space-y-4">
                                {[
                                    { icon: Brain, text: '4 Agentes IA: AURA, COPILOTO, WATCHDOG, SCRIBE' },
                                    { icon: AlertTriangle, text: 'Alertas FDTL automáticas (FAA, EASA, DGAC, DGCA)' },
                                    { icon: Lock, text: 'Autenticación JWT con 16+ roles especializados' },
                                    { icon: ShieldCheck, text: 'Reportes PDF para auditorias regulatorias' },
                                    { icon: Globe, text: 'Multi-tenant SaaS con aislamiento total de datos' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm">
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <item.icon className="w-4 h-4 text-primary" />
                                        </div>
                                        <span className="text-muted-foreground">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        </Reveal>
                        <Reveal delay={150}>
                        <div className="relative">
                            <Card className="card-aviation overflow-hidden">
                                <CardContent className="p-0">
                                    <div className="bg-muted/50 px-4 py-2.5 border-b border-border/50 flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                                        <span className="ml-2 text-[10px] font-mono text-muted-foreground">AeroOps Platform</span>
                                    </div>
                                    <div className="p-5 space-y-4 font-mono text-xs">
                                        <div className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5"><Brain className="w-3.5 h-3.5 text-primary" /></div>
                                            <div className="bg-muted/50 rounded-lg px-3 py-2 text-muted-foreground leading-relaxed">
                                                Verifica el FDTL del Capitan Garcia y el estado del turnaround en Gate B3.
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"><Sparkles className="w-3.5 h-3.5 text-emerald-500" /></div>
                                            <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg px-3 py-2 text-muted-foreground leading-relaxed">
                                                <span className="text-emerald-500 font-bold">AURA:</span> Cap. Garcia tiene 6.2h/9h en 24h (69%). Turnaround Gate B3: 8/13 servicios completados, tiempo 28 min. Equipo Alpha en rampa.
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* POR QUE AEROOPS — historia del fundador */}
            <section className="py-20 px-4 bg-muted/30">
                <div className="max-w-3xl mx-auto">
                    <Reveal>
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase mb-4">
                                <Quote className="w-3.5 h-3.5" /> Por Qué AeroOps
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="landing-founder-title">
                                Construido Desde Adentro de la Operación
                            </h2>
                        </div>
                        <Card className="card-aviation relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
                            <CardContent className="p-8 sm:p-10">
                                <Quote className="w-8 h-8 text-primary/30 mb-4" />
                                <p className="text-base sm:text-lg text-foreground leading-relaxed font-medium">
                                    {founderStory.quote}
                                </p>
                                <div className="mt-8 space-y-3">
                                    {founderStory.points.map((p, i) => (
                                        <div key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                                            <span>{p}</span>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-8 text-sm sm:text-base text-muted-foreground leading-relaxed border-t border-border/50 pt-6">
                                    {founderStory.closing}
                                </p>
                                <div className="mt-6 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <UserCheck className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold">Fundador de AeroOps</div>
                                        <div className="text-xs text-muted-foreground">Ex despachador, supervisor de despacho y CCO</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </Reveal>
                </div>
            </section>

            {/* CONFIANZA Y CUMPLIMIENTO — dedicada, separada de las tarjetas de modulos */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">
                            <ShieldCheck className="w-3.5 h-3.5" /> Confianza y Cumplimiento
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="landing-trust-title">
                            En Lo Que Puedes Confiar
                        </h2>
                        <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto">Un sistema que maneja datos de seguridad operacional necesita más que buenas intenciones — necesita garantías concretas.</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {trustPoints.map((t, i) => (
                            <Reveal key={t.title} delay={i * 90}>
                                <Card className="card-aviation h-full">
                                    <CardContent className="p-6">
                                        <div className="w-11 h-11 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                                            <t.icon className="w-5 h-5 text-emerald-500" />
                                        </div>
                                        <h3 className="font-bold text-sm font-['Barlow_Condensed'] uppercase tracking-wide">{t.title}</h3>
                                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{t.desc}</p>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMO EMPEZAR — pasos del proceso */}
            <section className="py-20 px-4 bg-muted/30">
                <div className="max-w-5xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase mb-4">
                            <Rocket className="w-3.5 h-3.5" /> Cómo Empezar
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="landing-howto-title">
                            De Hojas de Cálculo a Operando en AeroOps
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
                        {onboardingSteps.map((s, i) => (
                            <Reveal key={s.title} delay={i * 100}>
                                <div className="relative">
                                    {i < onboardingSteps.length - 1 && (
                                        <div className="hidden lg:block absolute top-7 left-[60%] w-full h-px bg-border/60" />
                                    )}
                                    <div className="relative flex flex-col items-start">
                                        <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 relative z-10 bg-background">
                                            <s.icon className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="text-[10px] font-bold text-primary tracking-widest uppercase mb-1">Paso {i + 1}</div>
                                        <h3 className="font-bold text-sm font-['Barlow_Condensed'] uppercase tracking-wide">{s.title}</h3>
                                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PLANS — tabbed */}
            <section id="planes" className="py-20 px-4">
                <div className="max-w-5xl mx-auto">
                    <Tabs value={activePlansTab} onValueChange={setActivePlansTab}>
                        <Reveal className="text-center mb-14">
                            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] mb-6" data-testid="landing-plans-title">Planes y Precios</h2>
                            <TabsList className="mx-auto">
                                <TabsTrigger value="airlines" data-testid="plans-tab-airlines"><Plane className="w-3.5 h-3.5 mr-1.5" />Airlines</TabsTrigger>
                                <TabsTrigger value="ground" data-testid="plans-tab-ground"><Truck className="w-3.5 h-3.5 mr-1.5" />Ground Handling</TabsTrigger>
                            </TabsList>
                            <p className="text-sm text-muted-foreground mt-4 max-w-xl mx-auto">Aerolíneas: pagas por aeronave facturable al mes (todas salvo las almacenadas), según el plan que corresponde a tu flota. Sumas add-ons opcionales (Tripulaciones, Mantenimiento, IA, Predictivo, Revenue) con precio fijo mensual. Pago anual: 2 meses sin costo.</p>
                        </Reveal>
                        <TabsContent value="airlines" className="mt-0">
                            <div className="grid md:grid-cols-3 gap-6">
                                {airlinePlans.map((p) => (
                                    <Card key={p.name} className={`card-aviation relative overflow-hidden ${p.highlight ? 'border-primary/50 shadow-lg shadow-primary/5' : ''}`} data-testid={`plan-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                                        {p.highlight && <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />}
                                        <CardContent className="p-6 pt-8">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="font-bold text-lg font-['Barlow_Condensed'] uppercase">{p.name}</h3>
                                                {p.highlight && <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-[10px] font-bold rounded-full uppercase">Popular</span>}
                                            </div>
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-3xl font-bold font-mono">{p.price}</span>
                                                <span className="text-sm text-muted-foreground">{p.period}</span>
                                            </div>
                                            <div className="mt-6 grid grid-cols-3 gap-2 p-3 rounded-lg bg-muted/50">
                                                {p.limits.map(([val, label]) => (
                                                    <div key={label} className="text-center">
                                                        <div className="text-lg font-bold font-mono text-primary">{val}</div>
                                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            <ul className="mt-5 space-y-2.5">
                                                {p.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-primary flex-shrink-0" />{f}</li>)}
                                            </ul>
                                            <Button className={`w-full mt-6 ${p.highlight ? 'btn-primary' : ''}`} variant={p.highlight ? 'default' : 'outline'} onClick={() => scrollTo('contact')}>Comenzar</Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                            {/* Auditoria 9 - Item 2: tabla de add-ons con precios reales */}
                            {airlineAddons.length > 0 && (
                                <Reveal className="mt-10">
                                    <Card className="card-aviation" data-testid="landing-addons-airlines">
                                        <CardContent className="p-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Modulos Add-on Disponibles</h3>
                                                <span className="text-[10px] text-muted-foreground">Precio fijo mensual por aerolínea</span>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {airlineAddons.map((a) => (
                                                    <div key={a.key} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border/50 hover:border-primary/40 transition-colors">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-bold">{a.label}</div>
                                                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</div>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <div className="text-base font-bold font-mono text-primary">${a.price_monthly_usd}</div>
                                                            <div className="text-[10px] text-muted-foreground uppercase">/mes</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            )}
                        </TabsContent>
                        <TabsContent value="ground" className="mt-0">
                            <div className="grid md:grid-cols-3 gap-6">
                                {ghPlans.map((p) => (
                                    <Card key={p.name} className={`card-aviation relative overflow-hidden ${p.highlight ? 'border-amber-500/50 shadow-lg shadow-amber-500/5' : ''}`} data-testid={`plan-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                                        {p.highlight && <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" />}
                                        <CardContent className="p-6 pt-8">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="font-bold text-lg font-['Barlow_Condensed'] uppercase">{p.name}</h3>
                                                {p.highlight && <span className="px-2.5 py-0.5 bg-amber-500/10 text-amber-500 text-[10px] font-bold rounded-full uppercase">Popular</span>}
                                            </div>
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-3xl font-bold font-mono">{p.price}</span>
                                                <span className="text-sm text-muted-foreground">{p.period}</span>
                                            </div>
                                            <div className="mt-6 grid grid-cols-3 gap-2 p-3 rounded-lg bg-muted/50">
                                                {p.limits.map(([val, label]) => (
                                                    <div key={label} className="text-center">
                                                        <div className="text-lg font-bold font-mono text-amber-500">{val}</div>
                                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            <ul className="mt-5 space-y-2.5">
                                                {p.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{f}</li>)}
                                            </ul>
                                            <Button className={`w-full mt-6 ${p.highlight ? 'bg-amber-500 hover:bg-amber-600 text-black' : ''}`} variant={p.highlight ? 'default' : 'outline'} onClick={() => scrollTo('contact')}>Comenzar</Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                            {/* Auditoria 9 - Item 2 + 5: add-ons GH desde catalogo publico */}
                            {ghAddons.length > 0 && (
                                <Reveal className="mt-10">
                                    <Card className="card-aviation" data-testid="landing-addons-ground">
                                        <CardContent className="p-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Modulos Add-on Ground Handling</h3>
                                                <span className="text-[10px] text-muted-foreground">Se suman al plan base</span>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {ghAddons.map((a) => (
                                                    <div key={a.key} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border/50 hover:border-amber-500/40 transition-colors">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-bold">{a.label}</div>
                                                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</div>
                                                        </div>
                                                        {a.coming_soon ? (
                                                            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-bold uppercase tracking-wider" data-testid={`landing-addon-soon-${a.key}`}>Próximamente</span>
                                                        ) : (
                                                            <div className="text-right flex-shrink-0">
                                                                <div className="text-base font-bold font-mono text-amber-500">${a.price_monthly_usd}</div>
                                                                <div className="text-[10px] text-muted-foreground uppercase">/mes</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 px-4 bg-muted/30">
                <div className="max-w-2xl mx-auto">
                    <Reveal className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase mb-4">
                            <HelpCircle className="w-3.5 h-3.5" /> Preguntas Frecuentes
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="landing-faq-title">
                            Dudas Antes de Empezar
                        </h2>
                    </Reveal>
                    <Reveal delay={100}>
                        <Card className="card-aviation">
                            <CardContent className="p-2 sm:p-4">
                                <Accordion type="single" collapsible className="w-full" data-testid="landing-faq-accordion">
                                    {faqItems.map((item, i) => (
                                        <AccordionItem key={i} value={`faq-${i}`} className="border-border/50">
                                            <AccordionTrigger className="text-left text-sm font-bold px-2 hover:no-underline">{item.q}</AccordionTrigger>
                                            <AccordionContent className="text-sm text-muted-foreground px-2 leading-relaxed">{item.a}</AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </CardContent>
                        </Card>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" className="py-20 px-4">
                <div className="max-w-2xl mx-auto">
                    <Reveal className="text-center mb-10">
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="landing-contact-title">Solicita tu Demo</h2>
                        <p className="text-sm text-muted-foreground mt-3">Completa el formulario y nuestro equipo te contactara en menos de 24 horas.</p>
                    </Reveal>
                    <Reveal delay={100}>
                    <Card className="card-aviation" data-testid="lead-capture-form">
                        <CardContent className="p-6 sm:p-8">
                            <form onSubmit={handleLeadSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Tipo de Servicio</Label>
                                    <Select value={leadForm.service_type} onValueChange={v => setLeadForm({...leadForm, service_type: v})}>
                                        <SelectTrigger data-testid="lead-service-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="airline">Aerolinea / Operador Aereo</SelectItem>
                                            <SelectItem value="ground_handler">Empresa de Asistencia en Tierra</SelectItem>
                                            <SelectItem value="both">Ambos Servicios</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2"><Label>Empresa</Label><Input value={leadForm.company_name} onChange={(e) => setLeadForm({ ...leadForm, company_name: e.target.value })} required placeholder="Mi Empresa" className="input-aviation" data-testid="lead-company" /></div>
                                    <div className="space-y-2"><Label>Nombre de Contacto</Label><Input value={leadForm.contact_name} onChange={(e) => setLeadForm({ ...leadForm, contact_name: e.target.value })} required placeholder="Juan Perez" className="input-aviation" data-testid="lead-contact" /></div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2"><Label>Email</Label><Input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} required placeholder="ops@empresa.com" className="input-aviation" data-testid="lead-email" /></div>
                                    <div className="space-y-2"><Label>Telefono</Label><Input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="+502 5555 1234" className="input-aviation" data-testid="lead-phone" /></div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Pais</Label>
                                        <Select value={leadForm.country} onValueChange={(v) => setLeadForm({ ...leadForm, country: v })}>
                                            <SelectTrigger className="input-aviation" data-testid="lead-country"><SelectValue placeholder="Selecciona tu país" /></SelectTrigger>
                                            <SelectContent className="max-h-72">
                                                {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2"><Label>{leadForm.service_type === 'ground_handler' ? 'Turnarounds mensuales' : 'Tamano de Flota'}</Label><Input type="number" value={leadForm.fleet_size} onChange={(e) => setLeadForm({ ...leadForm, fleet_size: e.target.value })} placeholder={leadForm.service_type === 'ground_handler' ? '500' : '5'} className="input-aviation" data-testid="lead-fleet" /></div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Mensaje (opcional)</Label>
                                    <textarea value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })} placeholder="Cuentanos sobre tus necesidades operacionales..." className="w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary font-mono min-h-[80px] resize-none" data-testid="lead-message" />
                                </div>
                                <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="lead-submit">
                                    {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enviar Solicitud
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                    </Reveal>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="py-10 px-4 border-t border-border/30">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-2.5">
                            <img src={LOGO_URL} alt="AeroOps" className="w-7 h-7 object-contain" />
                            <span className="text-sm font-bold uppercase font-['Barlow_Condensed'] tracking-wider">AeroOps</span>
                        </div>
                        <div className="flex items-center gap-6 text-xs text-muted-foreground">
                            <button onClick={() => navigate('/login')} className="hover:text-foreground transition-colors duration-200" data-testid="footer-login-link">Acceder</button>
                            <button onClick={() => scrollTo('contact')} className="hover:text-foreground transition-colors duration-200" data-testid="footer-contact-link">Contacto</button>
                            <a href="/terminos" className="hover:text-foreground transition-colors duration-200" data-testid="footer-terms-link">Términos</a>
                            <a href="/privacidad" className="hover:text-foreground transition-colors duration-200" data-testid="footer-privacy-link">Privacidad</a>
                            <a href="/cookies" className="hover:text-foreground transition-colors duration-200" data-testid="footer-cookies-link">Cookies</a>
                        </div>
                    </div>
                    <div className="mt-6 flex flex-col sm:flex-row items-center sm:justify-between gap-2 text-[11px] text-muted-foreground/60">
                        <div className="text-center sm:text-left">
                            &copy; {new Date().getFullYear()} AeroOps. Todos los derechos reservados.
                            <span className="hidden sm:inline"> &mdash; Gestion operacional para aerolineas y asistencia en tierra.</span>
                        </div>
                        {/* FASE C Audit 2: acceso admin discreto, no promocionado */}
                        <button
                            onClick={() => navigate('/admin/login')}
                            className="text-muted-foreground/50 hover:text-muted-foreground text-[10px] tracking-wide"
                            data-testid="footer-admin-link"
                        >
                            Acceso administradores
                        </button>
                    </div>
                </div>
            </footer>
        </div>
    );
}
